export const TABLE_STATE_PREFIX = 'mp_trend.table.';

type TableStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;
export type TableStateBucket = 'session' | 'sorting' | 'filters';

type TableStorages = {
  session: TableStorage;
  local?: TableStorage | null;
};

export type TableStatePolicy<T extends object> = {
  sortingKeys?: readonly (keyof T)[];
  filterKeys?: readonly (keyof T)[];
  persistSorting?: boolean;
  persistFilters?: boolean;
};

export function tableStateKey(userId: string, tableId: string): string {
  return `${TABLE_STATE_PREFIX}${userId}.${tableId}`;
}

export function tableStateBucketKey(
  userId: string,
  tableId: string,
  bucket: TableStateBucket,
): string {
  return `${tableStateKey(userId, tableId)}.${bucket}`;
}

function readRawState<T extends object>(storage: TableStorage, key: string): Partial<T> {
  const raw = storage.getItem(key);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Partial<T>;
  } catch {
    return {};
  }
}

function pickKeys<T extends object>(state: T, keys: readonly (keyof T)[]): Partial<T> {
  const picked: Partial<T> = {};
  keys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(state, key)) {
      picked[key] = state[key];
    }
  });
  return picked;
}

function omitKeys<T extends object>(state: T, keys: Set<keyof T>): Partial<T> {
  const omitted: Partial<T> = {};
  (Object.keys(state) as Array<keyof T>).forEach((key) => {
    if (!keys.has(key)) {
      omitted[key] = state[key];
    }
  });
  return omitted;
}

function writeRawState<T extends object>(storage: TableStorage, key: string, value: Partial<T>): void {
  if (Object.keys(value).length === 0) {
    storage.removeItem(key);
    return;
  }
  storage.setItem(key, JSON.stringify(value));
}

export function readStoredTableState<T extends object>(
  storage: TableStorage,
  userId: string,
  tableId: string,
  defaults: T,
): T {
  const raw = storage.getItem(tableStateKey(userId, tableId));
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return defaults;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

export function readStoredTableStateWithPolicy<T extends object>(
  storages: TableStorages,
  userId: string,
  tableId: string,
  defaults: T,
  policy: TableStatePolicy<T>,
): T {
  const sortingStorage = policy.persistSorting ? (storages.local ?? storages.session) : storages.session;
  const filtersStorage = policy.persistFilters ? (storages.local ?? storages.session) : storages.session;
  const sortingKey = tableStateBucketKey(userId, tableId, 'sorting');
  const filtersKey = tableStateBucketKey(userId, tableId, 'filters');
  return {
    ...readStoredTableState(storages.session, userId, tableId, defaults),
    ...readRawState<T>(storages.session, tableStateBucketKey(userId, tableId, 'session')),
    ...(policy.persistSorting ? readRawState<T>(storages.session, sortingKey) : {}),
    ...(policy.persistFilters ? readRawState<T>(storages.session, filtersKey) : {}),
    ...readRawState<T>(sortingStorage, sortingKey),
    ...readRawState<T>(filtersStorage, filtersKey),
  };
}

export function writeStoredTableState<T extends object>(
  storage: TableStorage,
  userId: string,
  tableId: string,
  state: T,
): void {
  storage.setItem(tableStateKey(userId, tableId), JSON.stringify(state));
}

export function writeStoredTableStateWithPolicy<T extends object>(
  storages: TableStorages,
  userId: string,
  tableId: string,
  state: T,
  policy: TableStatePolicy<T>,
): void {
  const sortingKeys = policy.sortingKeys ?? [];
  const filterKeys = policy.filterKeys ?? [];
  const classifiedKeys = new Set<keyof T>([...sortingKeys, ...filterKeys]);
  const local = storages.local ?? null;
  const sortingKey = tableStateBucketKey(userId, tableId, 'sorting');
  const filtersKey = tableStateBucketKey(userId, tableId, 'filters');

  storages.session.removeItem(tableStateKey(userId, tableId));
  local?.removeItem(tableStateKey(userId, tableId));

  if (policy.persistSorting && local) {
    writeRawState(local, sortingKey, pickKeys(state, sortingKeys));
    storages.session.removeItem(sortingKey);
  } else {
    writeRawState(storages.session, sortingKey, pickKeys(state, sortingKeys));
    local?.removeItem(sortingKey);
  }

  if (policy.persistFilters && local) {
    writeRawState(local, filtersKey, pickKeys(state, filterKeys));
    storages.session.removeItem(filtersKey);
  } else {
    writeRawState(storages.session, filtersKey, pickKeys(state, filterKeys));
    local?.removeItem(filtersKey);
  }

  writeRawState(
    storages.session,
    tableStateBucketKey(userId, tableId, 'session'),
    omitKeys(state, classifiedKeys),
  );
}

export function clearStoredTableStates(
  storage: TableStorage = sessionStorage,
  options: { userId?: string; buckets?: readonly TableStateBucket[] } = {},
): void {
  const prefix = options.userId ? `${TABLE_STATE_PREFIX}${options.userId}.` : TABLE_STATE_PREFIX;
  const buckets = options.buckets ?? [];
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key?.startsWith(prefix)) continue;
    if (buckets.length > 0 && !buckets.some((bucket) => key.endsWith(`.${bucket}`))) continue;
    keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
}
